import get from 'lodash/get';
import moment from 'moment';

// TODO: Replace with types lib
import { DataType, NumericDataType } from '../../@types';

export const JOIN_SEPARATOR = '; ';
export const HANDLEBARS_PATTERN = /^{{.*}}$/;
export const STRING_OR_NUMERIC_PATTERN = /^'.*'$|^\d+(\.\d+)?([eE][-+]?\d+)?$/;
export const REPLACE_OP_PATTERN = /{{(.+?)}}/;

export const CONCAT_OP_PATTERN = /^{{0,2}concat\((.+)+\)}{0,2}$/;
export const COALESCE_OP_PATTERN = /^{{0,2}coalesce\((.+)+\)}{0,2}$/;
export const JOIN_OP_PATTERN = /^{{0,2}join\(('.+'),(.+)+\)}{0,2}$/;
export const REASON_OP_PATTERN =
  /^{{0,2}format\('(reason)',(.+?),(.+?)(?:,(.+)){0,1}\)}{0,2}$/;
export const DATE_TO_UNIX_OP_PATTERN = /^{{0,2}dateToUnix\(([^,]+)\)}{0,2}$/;
export const HOUR_RANGE_OP_PATTERN = /^{{0,2}hourRange\(([^,]+)\)}{0,2}$/;
export const UPPERCASE_OP_PATTERN = /^{{0,2}uppercase\((.+)\)}{0,2}$/;
export const LOWERCASE_OP_PATTERN = /^{{0,2}lowercase\((.+)\)}{0,2}$/;

// Math operations
export const SUM_OP_PATTERN = /^{{0,2}Math.sum\((.+)+\)}{0,2}$/;
export const SUBTRACT_OP_PATTERN = /^{{0,2}Math.subtract\((.+)+\)}{0,2}$/;
export const MULTIPLY_OP_PATTERN = /^{{0,2}Math.multiply\((.+)+\)}{0,2}$/;
export const DIVIDE_OP_PATTERN =
  /^{{0,2}Math.divide\(([^,]+),\s*([^,]+)\)}{0,2}$/;

// Legacy patterns
export const LEGACY_CONCAT_OP_PATTERN = /concat\((.+)+\)/;
export const LEGACY_JOIN_OP_PATTERN = /join\(('.+'),(.+)+\)/;
export const LEGACY_REASON_OP_PATTERN = /format\('(reason)',(.+),(.+)+\)/;

/**
 * Given a string containing a function call, returns an array containing the list of parameters
 * @private
 */
const extractParamsFromString = (funcString: string): Array<string> => {
  const params: Array<string> = [];

  // Extract everything within the parentheses
  const paramString = funcString.substring(
    funcString.indexOf('(') + 1,
    funcString.lastIndexOf(')'),
  );

  let currentParam = '';
  let nestedLevel = 0;
  let inString = false;
  let stringChar = '';

  // Iterate over each character in the parameter string
  for (let i = 0; i < paramString.length; i += 1) {
    const char = paramString[i];

    if (inString) {
      if (char === stringChar) {
        // We've reached the end of the current string
        inString = false;
        currentParam += char;
      } else if (char === '\\' && i < paramString.length - 1) {
        // Handle escape character
        const nextChar = paramString[i + 1];
        if (nextChar === stringChar || nextChar === '\\') {
          currentParam += nextChar;
          i += 1;
        } else {
          currentParam += char;
        }
      } else {
        currentParam += char;
      }
    } else if (char === ',' && nestedLevel === 0) {
      // A comma marks the end of the current parameter
      params.push(currentParam.trim());
      currentParam = '';
    } else {
      // Append the character to the current parameter
      currentParam += char;

      if (char === '"' || char === "'") {
        // We've encountered the start of a string
        inString = true;
        stringChar = char;
      } else if (char === '(') {
        // Increase the nested level when we encounter an opening parenthesis
        nestedLevel += 1;
      } else if (char === ')') {
        // Decrease the nested level when we encounter a closing parenthesis
        nestedLevel -= 1;
      }
    }
  }

  // Add the final parameter
  params.push(currentParam.trim());

  return params;
};

/**
 * Parses a template string using a data object (usually returned from the API)
 * The following templates are currently supported:
 * - `concat(v1, v2, v3)`: concatenate all fields
 * - `join(', ', v1, v2)`: join all fields using the separator defined in the first arg
 * - `format('reason', v1, v2)`: format a reason obj in the format parent (child)
 * - `$pathName`: perform a _.get() in the data using the `pathName`
 *
 * @private
 * @param data
 * @param value
 */
const legacyParseTemplate = (
  data: Record<string, unknown>,
  value: string,
): string | null => {
  // Concatenation operation
  if (LEGACY_CONCAT_OP_PATTERN.test(value)) {
    const match = LEGACY_CONCAT_OP_PATTERN.exec(value);
    if (!match) return value;

    const parts = match[1].split(',');
    return parts.map((p) => legacyParseTemplate(data, p.trim())).join('');
  }

  // Join operation
  if (LEGACY_JOIN_OP_PATTERN.test(value)) {
    const match = LEGACY_JOIN_OP_PATTERN.exec(value);
    if (!match) return value;

    const divisor = match[1];
    const parts = match[2].split(',');
    return parts
      .map((p) => legacyParseTemplate(data, p.trim()))
      .join(divisor.replace(/'/g, ''));
  }

  // Format reason operation
  if (LEGACY_REASON_OP_PATTERN.test(value)) {
    const match = LEGACY_REASON_OP_PATTERN.exec(value);
    if (!match) return value;

    const parent = legacyParseTemplate(data, match[2].trim());
    const child = legacyParseTemplate(data, match[3].trim());
    return parent === match[2].trim() || parent?.includes('n/a')
      ? child
      : `${parent} (${child})`;
  }

  // Replace operation
  if (REPLACE_OP_PATTERN.test(value)) {
    const match = REPLACE_OP_PATTERN.exec(value);
    if (!match) return value;
    let resultValue = value;
    match.slice(1).forEach((group) => {
      resultValue = resultValue.replace(
        `{{${group}}}`,
        get(data, group, group) as string,
      );
    });

    return resultValue;
  }

  // Replace operation
  /**
   * @deprecated - use REPLACE_OP_PATTERN instead of $ to define a replace pattern
   */
  if (value.startsWith('$')) {
    return get(data, value.replace('$', ''), value) as string | null;
  }

  // strip ' char from the string
  return value.replace(/'/g, '');
};

/**
 * Parses a template string using a data object (usually returned from the API)
 * The following templates are currently supported:
 * - `dateToUnix(datetime)`: convert a datetime like '2023-01-01T08:40:00+00:00' to unix format in seconds
 * - `hourRange(datetime)`: convert a datetime or number to hour range
 * - `Math.sum(...args)`: perform a sum between multiple args
 * - `Math.subtract(v1, v2)`: perform a subtraction between two args
 * - `Math.multiply(..args)`: perform a multiplication between multiple args
 * - `Math.divide(v1, v2)`: perform a division between two args
 * - `concat(...args)`: concatenate all fields
 * - `coalesce(...args)`: return the first truthy value
 * - `join(', ', v1, v2)`: join all fields using the separator defined in the first arg
 * - `format('reason', v1, v2)`: format a reason obj in the format parent (child)
 * - `$pathName`: perform a _.get() in the data using the `pathName`
 *
 * @private
 * @param data
 * @param value
 */
const parseTemplate = (
  data: Record<string, unknown>,
  value: string | number,
  isVariable = false,
): string | number | null => {
  if (typeof value === 'number') return value;

  // Coalescence operation
  if (COALESCE_OP_PATTERN.test(value)) {
    const match = COALESCE_OP_PATTERN.exec(value);
    if (!match) return value;
    const parts = extractParamsFromString(value);
    const truthy = parts.find((p) => {
      const result = parseTemplate(
        data,
        p.trim(),
        !STRING_OR_NUMERIC_PATTERN.test(p.trim()),
      );
      return (
        typeof result !== 'object' &&
        result !== undefined &&
        !Number.isNaN(result) &&
        // Is not the template or is a string or numeric
        (result !== p || STRING_OR_NUMERIC_PATTERN.test(p.trim()))
      );
    });

    if (truthy) {
      return parseTemplate(
        data,
        truthy.trim(),
        !STRING_OR_NUMERIC_PATTERN.test(truthy.trim()),
      );
    }
    return value;
  }

  // Concatenation operation
  if (CONCAT_OP_PATTERN.test(value)) {
    const match = CONCAT_OP_PATTERN.exec(value);
    if (!match) return value;
    const parts = extractParamsFromString(value);

    return parts
      .map((p) =>
        parseTemplate(
          data,
          p.trim(),
          !STRING_OR_NUMERIC_PATTERN.test(p.trim()),
        ),
      )
      .join('');
  }

  // Join operation
  if (JOIN_OP_PATTERN.test(value)) {
    const match = JOIN_OP_PATTERN.exec(value);
    if (!match) return value;
    const divisor = match[1];
    const parts = extractParamsFromString(value).slice(1);

    return parts
      .map((p) =>
        parseTemplate(
          data,
          p.trim(),
          !STRING_OR_NUMERIC_PATTERN.test(p.trim()),
        ),
      )
      .filter((item) => !!item)
      .join(divisor.replace(/'/g, ''));
  }

  // Uppercase operation
  if (UPPERCASE_OP_PATTERN.test(value)) {
    const match = UPPERCASE_OP_PATTERN.exec(value);
    if (!match) return value;
    const part = extractParamsFromString(value)[0];

    return String(
      parseTemplate(
        data,
        part.trim(),
        !STRING_OR_NUMERIC_PATTERN.test(part.trim()),
      ),
    ).toUpperCase();
  }

  // Lowercase operation
  if (LOWERCASE_OP_PATTERN.test(value)) {
    const match = LOWERCASE_OP_PATTERN.exec(value);
    if (!match) return value;
    const part = extractParamsFromString(value)[0];

    return String(
      parseTemplate(
        data,
        part.trim(),
        !STRING_OR_NUMERIC_PATTERN.test(part.trim()),
      ),
    ).toLowerCase();
  }

  // Format reason operation
  if (REASON_OP_PATTERN.test(value)) {
    const match = REASON_OP_PATTERN.exec(value);
    if (!match) return value;
    const parent = parseTemplate(
      data,
      match[2].trim(),
      !STRING_OR_NUMERIC_PATTERN.test(match[2].trim()),
    ) as string | null;
    const child = parseTemplate(
      data,
      match[3].trim(),
      !STRING_OR_NUMERIC_PATTERN.test(match[3].trim()),
    );
    const customization =
      match[4]?.trim() &&
      parseTemplate(
        data,
        match[4].trim(),
        !STRING_OR_NUMERIC_PATTERN.test(match[4].trim()),
      );
    return parent === match[2].trim() || parent?.includes('n/a')
      ? `${child}${
          customization && customization !== match[4]?.trim()
            ? ` (${customization})`
            : ''
        }`
      : `${parent} (${child}${
          customization !== match[4]?.trim() ? ` - ${customization}` : ''
        })`;
  }

  // Date to interval operation
  if (DATE_TO_UNIX_OP_PATTERN.test(value)) {
    const match = DATE_TO_UNIX_OP_PATTERN.exec(value);
    if (!match) return value;
    const parts = extractParamsFromString(value);

    const parsedPart = parseTemplate(
      data,
      parts[0].trim(),
      !STRING_OR_NUMERIC_PATTERN.test(parts[0].trim()),
    );

    if (typeof parsedPart === 'string') {
      return moment(parsedPart).unix();
    }
    return value;
  }

  // Date to hour range
  if (HOUR_RANGE_OP_PATTERN.test(value)) {
    const match = HOUR_RANGE_OP_PATTERN.exec(value);
    if (!match) return value;
    const parts = extractParamsFromString(value);

    const parsedPart = parseTemplate(
      data,
      parts[0].trim(),
      !STRING_OR_NUMERIC_PATTERN.test(parts[0].trim()),
    );

    if (typeof parsedPart === 'string' || typeof parsedPart === 'number') {
      const hour = Number(parsedPart) || moment(parsedPart).hour();
      return `${hour}h às ${hour === 23 ? 0 : hour + 1}h`;
    }
    return value;
  }

  // Sum operation
  if (SUM_OP_PATTERN.test(value)) {
    const match = SUM_OP_PATTERN.exec(value);
    if (!match) return value;
    const parts = extractParamsFromString(value);

    return parts
      .map((p) =>
        parseTemplate(
          data,
          p.trim(),
          !STRING_OR_NUMERIC_PATTERN.test(p.trim()),
        ),
      )
      .reduce((acc, curr) => Number(acc) + Number(curr), 0);
  }

  // Subtraction operation
  if (SUBTRACT_OP_PATTERN.test(value)) {
    const match = SUBTRACT_OP_PATTERN.exec(value);
    if (!match) return value;
    const parts = extractParamsFromString(value);

    const parsedParts = parts.map((p) =>
      parseTemplate(data, p.trim(), !STRING_OR_NUMERIC_PATTERN.test(p.trim())),
    );

    return Number(parsedParts[0]) - Number(parsedParts[1]);
  }

  // Multiplication operation
  if (MULTIPLY_OP_PATTERN.test(value)) {
    const match = MULTIPLY_OP_PATTERN.exec(value);
    if (!match) return value;
    const parts = extractParamsFromString(value);

    return parts
      .map((p) =>
        parseTemplate(
          data,
          p.trim(),
          !STRING_OR_NUMERIC_PATTERN.test(p.trim()),
        ),
      )
      .reduce((acc, curr) => Number(acc) * Number(curr), 1);
  }

  // Division operation
  if (DIVIDE_OP_PATTERN.test(value)) {
    const match = DIVIDE_OP_PATTERN.exec(value);
    if (!match) return value;
    const parts = extractParamsFromString(value);

    const parsedParts = parts.map((p) =>
      parseTemplate(data, p.trim(), !STRING_OR_NUMERIC_PATTERN.test(p.trim())),
    );

    return Number(parsedParts[0]) / Number(parsedParts[1]);
  }

  /**
   * Replace operation
   * @tutorial This operation should come after all others as it's pattern could match any string inside handlebars
   */
  if (REPLACE_OP_PATTERN.test(value)) {
    const match = REPLACE_OP_PATTERN.exec(value);
    if (!match) return value;
    let resultValue = value;
    match.slice(1).forEach((group) => {
      const retrievedValue = get(data, group, group);
      resultValue = resultValue.replace(
        `{{${group}}}`,
        typeof retrievedValue !== 'string'
          ? JSON.stringify(retrievedValue)
          : retrievedValue,
      );
    });

    return resultValue;
  }

  /**
   * Replace operation
   * @deprecated - use REPLACE_OP_PATTERN instead of $ to define a replace pattern
   */
  if (value.startsWith('$')) {
    return get(data, value.replace('$', ''), value) as string | null;
  }

  // Operation for variables without handlebars or $
  if (isVariable) {
    let resultValue = value;
    resultValue = get(data, value, value) as string;
    return resultValue;
  }

  // strip ' char from the string
  return value.replace(/^'|'$/g, '');
};

/**
 * Converts a value based on the defined type
 *
 * @param value
 * @param type
 */
export const cast = <T extends DataType>(
  value: string | string[] | null,
  type: T,
): T extends NumericDataType ? number : string | boolean => {
  /* @note sadly, any is necessary in this block for the generic type to work */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  if (value === null || value === 'null') return 'null' as any;

  if (type === 'boolean') {
    return (value === 'true') as any;
  }

  if (type === 'integer' || type === 'decimal') {
    const valueToParse = Array.isArray(value) ? value.join('') : value;
    if (type === 'integer') {
      return parseInt(valueToParse, 10) as any;
    }
    if (type === 'decimal') {
      return parseFloat(valueToParse) as any;
    }
  }

  if (type === 'interval') {
    // Check first string value position for a object notation duration
    try {
      const durationValue =
        typeof value === 'string' && value.charAt(0) === '{'
          ? JSON.parse(value)
          : value;
      const duration = moment.duration(durationValue);
      return duration.asSeconds() as any;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse interval', value);
      return `${value}` as any;
    }
  }

  if (type === 'hstore') {
    return (value as string).replace(/"pt-BR"=>"([^"]+)"/g, '$1') as any;
  }

  // Parse string-array type
  if (type === 'string-array') {
    if (Array.isArray(value)) {
      return value.join(JOIN_SEPARATOR) as any;
    }
    // Check first string value position for an JSON array
    if (typeof value === 'string' && value.charAt(0) === '[') {
      try {
        return JSON.parse(value).join(JOIN_SEPARATOR) as any;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse string-array', value);
        return `${value}` as any;
      }
    }
    // This is probably an string array `v1,v2`
    if (!value.includes('{')) {
      return value.split(',').join(JOIN_SEPARATOR) as any;
    }
    // This is probably a DB array `{v1,v2}`
    return value
      .slice(1, -1)
      .replace(/(?:([^\\])",)|(?:([^"]),([^\s]))/g, '$1$2$sep;$3')
      .split('$sep;')
      .map((e) => e.trim().replace(/^(?:,|")|(?:,|")$/g, ''))
      .join(JOIN_SEPARATOR) as any;
  }

  return `${value}` as any;
  /* eslint-enable @typescript-eslint/no-explicit-any */
};

/**
 * Parses a definition value using a data map and a defined type
 * @param data
 * @param value
 * @param type
 * @returns
 */
export const parseDataValue = <T extends DataType>(
  data: Record<string, unknown>,
  template: string,
  type: T,
): T extends NumericDataType ? number : string | boolean => {
  const value = HANDLEBARS_PATTERN.test(template)
    ? parseTemplate(data, template)
    : legacyParseTemplate(data, template);

  return typeof value === 'number'
    ? cast(String(value), type)
    : cast(value, type);
};

/**
 * Sanitizes the key property of the groupBy definition to be
 * safely used as object key.
 * @param key
 * @returns
 */
export const sanitizeKey = (key: string): string =>
  key.replace(/[[\]{}.]/g, '');
